/**
 * 防守模型（a2）—— 安全度评估
 *
 * 核心问题：**对手立直/鸣牌后，打哪张牌最安全？**
 *
 * 四层安全度（从绝对安全到危险）：
 *   ① 现物（100% 安全）—— 对手舍牌中出现过的牌，绝不会点炮（同巡振听规则）
 *   ② 筋牌 —— 两面听的理论排除（1-4-7 / 2-5-8 / 3-6-9 三条筋）
 *   ③ 壁 / 片（NC）—— 4 张中 3 张可见时，两面听的搭子无法成立
 *   ④ 其他 —— 按危险度估计（中张 > 边张 > 字牌）
 *
 * ⚠️ 筋牌不是绝对安全（只是"两面听点炮"概率大幅降低）
 *    对手可能是坎张/边张/单骑/双碰 —— 所以筋牌有"スジ引っかけ"(筋引挂)风险
 */

import {
  COPIES_PER_TILE,
  Counts,
  indexToName,
  isHonor,
  isTerminal,
  isYaochu,
  tileIndex,
  type Tile,
} from '../tile.js';
import type { GameState, PlayerState, Seat } from './state.js';
import { handToCounts } from './rules.js';

// ════════════════════════════════════════════════════════════════
// 类型
// ════════════════════════════════════════════════════════════════

/** 威胁源：一个正在进攻的对手 */
export interface Threat {
  /** 对手座位 */
  readonly seat: Seat;
  /** 是否立直（最强威胁） */
  readonly riichi: boolean;
  /** 副露数（鸣牌 = 有役可能） */
  readonly melds: number;
  /** 威胁权重（0-1，越高越危险） */
  readonly weight: number;
}

/** 安全度等级 */
export type SafetyLevel =
  | 'genbutsu' // 现物（绝对安全）
  | 'suji' // 筋牌（较安全）
  | 'wall' // 壁/片（较安全）
  | 'honor' // 字牌（相对安全，除役牌）
  | 'suji-trap' // 筋引挂危险
  | 'dangerous'; // 危险（中张无筋）

/** 单张牌的安全度评估 */
export interface SafetyEvaluation {
  /** 牌 */
  readonly tile: Tile;
  /** 综合安全度（0-100，越高越安全） */
  readonly safety: number;
  /** 安全等级 */
  readonly level: SafetyLevel;
  /** 对每个威胁源的安全度 */
  readonly perThreat: ReadonlyMap<Seat, number>;
  /** 说明 */
  readonly reasons: readonly string[];
}

// ════════════════════════════════════════════════════════════════
// 威胁识别
// ════════════════════════════════════════════════════════════════

/**
 * 识别场上的威胁源
 *
 * 权重规则：
 *   · 立直 → 1.0（最高）
 *   · 2 副露以上 → 0.6（可能有役、可能听牌）
 *   · 1 副露 → 0.3（警惕但不强）
 *   · 无副露无立直 → 0（门清未立直，一般不点炮）
 */
export function identifyThreats(state: GameState, selfSeat: Seat): Threat[] {
  const threats: Threat[] = [];

  for (const p of state.players) {
    if (p.seat === selfSeat) continue;
    if (p.won) continue;

    let weight = 0;
    if (p.riichi) weight = 1.0;
    else if (p.melds.length >= 2) weight = 0.6;
    else if (p.melds.length === 1) weight = 0.3;

    if (weight > 0) {
      threats.push({
        seat: p.seat,
        riichi: p.riichi,
        melds: p.melds.length,
        weight,
      });
    }
  }

  return threats;
}

// ════════════════════════════════════════════════════════════════
// 可见牌统计
// ════════════════════════════════════════════════════════════════

/**
 * 统计全部可见牌（自家手牌 + 所有舍牌 + 所有副露 + 宝牌指示）
 *
 * 用于计算壁/片的成立条件
 */
export function countVisibleTiles(state: GameState, selfSeat: Seat): number[] {
  const counts = new Array(34).fill(0);

  // 自家手牌
  for (const t of state.players[selfSeat].hand) counts[tileIndex(t)]++;

  // 所有舍牌（含自家）
  for (const p of state.players) {
    for (const t of p.discards) counts[tileIndex(t)]++;
  }

  // 所有副露
  for (const p of state.players) {
    for (const m of p.melds) {
      if (m.kind === 'shuntsu') {
        counts[m.start]++;
        counts[m.start + 1]++;
        counts[m.start + 2]++;
      } else {
        counts[m.start] += m.kind === 'kantsu' ? 4 : 3;
      }
    }
  }

  // 宝牌指示牌（只算已翻开的；未翻开的指示牌对玩家不可见，不该计入可见牌）
  for (const t of state.wall.doraIndicators.slice(0, state.wall.doraRevealed)) counts[tileIndex(t)]++;

  return counts;
}

// ════════════════════════════════════════════════════════════════
// 现物判定
// ════════════════════════════════════════════════════════════════

/**
 * 判断某张牌对某个对手是否是「现物」
 *
 * 现物 = 该对手的舍牌中出现过这张牌
 * （因为振听规则，他不可能荣和这张牌）
 */
export function isGenbutsuFor(tile: number, opponent: PlayerState): boolean {
  return opponent.discards.some((t) => tileIndex(t) === tile);
}

/** 某张牌对所有威胁源都是现物？*/
export function isGenbutsuForAll(tile: number, threats: readonly Threat[], state: GameState): boolean {
  return threats.every((th) => {
    const opp = state.players[th.seat];
    return isGenbutsuFor(tile, opp);
  });
}

// ════════════════════════════════════════════════════════════════
// 筋牌判定
// ════════════════════════════════════════════════════════════════

/** 筋的对应关系：牌 → 它所在的筋组（每 3 张一组：147 / 258 / 369）*/
function sujiGroup(tile: number): { suit: number; pos: number } | null {
  if (isHonor(tile)) return null;
  const suit = Math.floor(tile / 9);
  const pos = tile % 9; // 0-8
  return { suit, pos };
}

/**
 * 判断某张牌对某对手是否「筋」
 *
 * 原理：如果对手舍牌中有「同筋组的另一张」，则这张牌的两面听大概率不成立。
 *
 * 例：对手舍了 1m → 他不能用 23m 听 4m（因为 1m/4m 同筋）
 *     → 4m 对他相对安全（但仍可能是坎张/单骑/双碰）
 *
 * 筋组：{1,4,7} {2,5,8} {3,6,9}
 */
export function isSujiFor(tile: number, opponent: PlayerState): boolean {
  const g = sujiGroup(tile);
  if (!g) return false;

  const { suit, pos } = g;
  const group = pos % 3; // 0=147, 1=258, 2=369

  // 检查对手是否舍过同筋组的其他牌
  for (const t of opponent.discards) {
    const idx = tileIndex(t);
    if (isHonor(idx)) continue;
    if (Math.floor(idx / 9) !== suit) continue;
    if (idx % 3 === group && idx !== tile) return true;
  }
  return false;
}

/**
 * ⚠️ 筋引挂（スジ引っかけ）判定
 *
 * 对手可能故意先打 4m 再听 1m/7m（两面），这是"引挂"。
 * 判断依据：对手立直的那一巡附近打出的牌，其筋牌反而危险。
 */
export function isSujiTrap(tile: number, opponent: PlayerState): boolean {
  if (!opponent.riichi) return false;
  const g = sujiGroup(tile);
  if (!g) return false;

  // 简化：立直宣言牌（立直前最后一张舍牌）的筋牌 = 引挂危险
  // 完整实现需要看立直宣言牌的上下文
  const riichiTurn = opponent.riichiTurn;
  if (riichiTurn === null) return false;

  // 立直前一张舍牌
  const lastBeforeRiichi = opponent.discards[opponent.discards.length - 1];
  if (!lastBeforeRiichi) return false;

  const lastIdx = tileIndex(lastBeforeRiichi);
  if (isHonor(lastIdx)) return false;

  // 如果这张牌与立直宣言牌同筋 → 引挂危险
  if (Math.floor(lastIdx / 9) === g.suit && lastIdx % 3 === g.pos % 3) {
    return true;
  }
  return false;
}

// ════════════════════════════════════════════════════════════════
// 壁 / 片（No Chance）
// ════════════════════════════════════════════════════════════════

/**
 * 判断某张牌是否「壁」或「片」
 *
 * 原理：
 *   · 片（NC）：某张牌 4 张全部可见 → 它两侧的搭子无法成立
 *     例：4m 四张全见 → 23m/56m 两面听不可能（需要 4m）→ 1m/7m 相对安全
 *   · 壁：某张牌 3 张可见 → 只有 1 张在外
 *
 * 本函数返回"该牌的邻接牌是否因壁而无法做两面听"
 */
export function wallSafety(
  tile: number,
  visible: NumberArray,
): 'wall' | 'none' | false {
  if (isHonor(tile)) return false;
  const suitStart = Math.floor(tile / 9) * 9;
  const pos = tile - suitStart;

  // 检查这张牌的两侧"间四张"是否可见
  // 例：tile=1m（pos 0），看 4m（pos 3）—— 4m 满 4 张则 23m 搭子不可能
  //     tile=4m（pos 3），看 1m 和 7m
  const checkPairs: Array<[number, number]> = [];

  // 1-4-7 筋
  if (pos % 3 === 0) {
    // 1/4/7 → 看同筋的后两张
    if (pos + 3 <= 8) checkPairs.push([pos + 3, 4]);
    if (pos + 6 <= 8) checkPairs.push([pos + 6, 4]);
  } else if (pos % 3 === 1) {
    if (pos - 1 >= 0) checkPairs.push([pos - 1, 4]);
    if (pos + 2 <= 8) checkPairs.push([pos + 2, 4]);
  } else {
    if (pos - 2 >= 0) checkPairs.push([pos - 2, 4]);
    if (pos - 1 >= 0) checkPairs.push([pos - 1, 4]);
  }

  let wallCount = 0;
  let seen4 = 0;
  for (const [p, need] of checkPairs) {
    const idx = suitStart + p;
    const c = visible[idx] ?? 0;
    if (c >= need) seen4++;
    else if (c === 3) wallCount++;
  }

  if (seen4 > 0) return 'wall';
  if (wallCount > 0) return 'none';
  return false;
}

type NumberArray = readonly number[];

// ════════════════════════════════════════════════════════════════
// 综合安全度
// ════════════════════════════════════════════════════════════════

/**
 * 评估单张牌对单个威胁源的安全度（0-100）
 */
export function evaluateSafetyAgainst(
  tile: number,
  state: GameState,
  threat: Threat,
  visible: readonly number[],
): { safety: number; level: SafetyLevel; reasons: string[] } {
  const opponent = state.players[threat.seat];
  const reasons: string[] = [];

  // ① 现物 —— 绝对安全
  if (isGenbutsuFor(tile, opponent)) {
    return { safety: 100, level: 'genbutsu', reasons: [`${indexToName(tile)} 是现物`] };
  }

  // ② 字牌（非役牌）—— 相对安全（只能靠对子/刻子听）
  if (isHonor(tile)) {
    // 役牌（三元牌/场风/自风）危险度较高
    const isYakuhai = tile >= 31 && tile <= 33;
    // 场风/自风需要从 state 计算 —— 简化：只判三元牌
    if (isYakuhai) {
      reasons.push('三元牌（役牌，对手可能做刻子）');
      return { safety: 55, level: 'honor', reasons };
    }
    // 客风：如果有人舍过（≥2 张已见），安全度大幅提升
    const seenCount = state.players.reduce(
      (n, p) => n + p.discards.filter((t) => tileIndex(t) === tile).length,
      0,
    );
    if (seenCount >= 2) {
      reasons.push('客风（已见 2 张以上，几乎不可能成刻）');
      return { safety: 85, level: 'honor', reasons };
    }
    if (seenCount === 1) {
      reasons.push('客风（已见 1 张）');
      return { safety: 72, level: 'honor', reasons };
    }
    reasons.push('客风字牌（未见）');
    return { safety: 62, level: 'honor', reasons };
  }

  // ③ 筋引挂（立直对手的陷阱）
  if (isSujiTrap(tile, opponent)) {
    reasons.push('⚠️ 筋引挂危险（立直宣言牌的筋）');
    return { safety: 20, level: 'suji-trap', reasons };
  }

  // ④ 筋牌
  if (isSujiFor(tile, opponent)) {
    // 边张筋比中张筋更安全
    const pos = tile % 9;
    const isEdge = pos === 0 || pos === 8;
    reasons.push(`筋牌${isEdge ? '（边张）' : ''}`);
    // 边张筋 82 > 已见客风 72 > 中张筋 68 > 未见客风 62
    return { safety: isEdge ? 82 : 68, level: 'suji', reasons };
  }

  // ⑤ 壁 / 片
  const wall = wallSafety(tile, visible);
  if (wall === 'wall') {
    reasons.push('壁（邻接牌已满 4 张）');
    return { safety: 75, level: 'wall', reasons };
  }

  // ⑥ 无筋中张 —— 最危险
  const pos = tile % 9;
  const isMiddle = pos >= 2 && pos <= 6;
  const isEdgeTile = pos === 0 || pos === 8;
  const isNearEdge = pos === 1 || pos === 7;

  if (isMiddle) {
    reasons.push('无筋中张（最危险）');
    return { safety: 25, level: 'dangerous', reasons };
  }
  if (isNearEdge) {
    reasons.push('无筋次边张');
    return { safety: 40, level: 'dangerous', reasons };
  }
  reasons.push('无筋边张');
  return { safety: 55, level: 'dangerous', reasons };
}

/**
 * 评估某张牌的综合安全度（考虑所有威胁源）
 */
export function evaluateSafety(
  state: GameState,
  selfSeat: Seat,
  tile: Tile,
  threats?: readonly Threat[],
): SafetyEvaluation {
  const ts = threats ?? identifyThreats(state, selfSeat);
  const tileIdx2 = tileIndex(tile);
  const visible = countVisibleTiles(state, selfSeat);

  if (ts.length === 0) {
    return {
      tile,
      safety: 100,
      level: 'genbutsu',
      perThreat: new Map(),
      reasons: ['无威胁源'],
    };
  }

  const perThreat = new Map<Seat, number>();
  let minSafety = 100;
  const allReasons: string[] = [];
  let worstLevel: SafetyLevel = 'genbutsu';

  for (const th of ts) {
    const { safety, level, reasons } = evaluateSafetyAgainst(tileIdx2, state, th, visible);
    // 按威胁权重调整（立直对手的安全度更重要）
    const weighted = safety * th.weight + (1 - th.weight) * 100;
    perThreat.set(th.seat, Math.round(weighted));
    if (weighted < minSafety) {
      minSafety = weighted;
      worstLevel = level;
    }
    allReasons.push(`vs 座位${th.seat}: ${reasons.join('、')}`);
  }

  return {
    tile,
    safety: Math.round(minSafety),
    level: worstLevel,
    perThreat,
    reasons: allReasons,
  };
}

// ════════════════════════════════════════════════════════════════
// 安全牌推荐
// ════════════════════════════════════════════════════════════════

/**
 * 从手牌中选出最安全的牌（用于弃和）
 */
export function chooseSafestDiscard(
  state: GameState,
  selfSeat: Seat,
): SafetyEvaluation {
  const player = state.players[selfSeat];
  const threats = identifyThreats(state, selfSeat);

  const seen = new Set<number>();
  let best: SafetyEvaluation | null = null;

  for (const tile of player.hand) {
    const idx = tileIndex(tile);
    if (seen.has(idx)) continue;
    seen.add(idx);

    const ev = evaluateSafety(state, selfSeat, tile, threats);
    if (!best || ev.safety > best.safety) best = ev;
  }

  if (!best) throw new Error('手牌为空');
  return best;
}

/** 列出所有可打牌的安全度排序 */
export function rankBySafety(
  state: GameState,
  selfSeat: Seat,
): SafetyEvaluation[] {
  const player = state.players[selfSeat];
  const threats = identifyThreats(state, selfSeat);
  const seen = new Set<number>();
  const results: SafetyEvaluation[] = [];

  for (const tile of player.hand) {
    const idx = tileIndex(tile);
    if (seen.has(idx)) continue;
    seen.add(idx);
    results.push(evaluateSafety(state, selfSeat, tile, threats));
  }

  return results.sort((a, b) => b.safety - a.safety);
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 中文等级名 */
export function safetyLevelName(level: SafetyLevel): string {
  const names: Record<SafetyLevel, string> = {
    genbutsu: '现物',
    suji: '筋牌',
    wall: '壁',
    honor: '字牌',
    'suji-trap': '筋引挂⚠️',
    dangerous: '危险',
  };
  return names[level];
}

/** 格式化安全度评估 */
export function formatSafety(ev: SafetyEvaluation): string {
  return `打 ${indexToName(tileIndex(ev.tile))} → 安全度 ${ev.safety}（${safetyLevelName(ev.level)}）`;
}
